import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseUnsavedChangesProps {
  hasUnsavedChanges: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

export function useUnsavedChanges({
  hasUnsavedChanges,
  onConfirm,
  onCancel,
}: UseUnsavedChangesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        setIsModalOpen(true);
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button');

      if (button && button.getAttribute('data-nav-target')) {
        event.preventDefault();
        event.stopPropagation();
        setPendingNavigation(button.getAttribute('data-nav-target'));
        setIsModalOpen(true);
      }
    };

    window.history.pushState({ modalOpened: false }, '');

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClick, true);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges]);

  const handleReturn = () => {
    setIsModalOpen(true);
  };

  const confirmReturn = () => {
    setIsModalOpen(false);

    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else if (onConfirm) {
      onConfirm();
    } else {
      window.history.back();
    }
  };

  const cancelReturn = () => {
    if (onCancel) {
      onCancel();
    }
    setIsModalOpen(false);
    setPendingNavigation(null);
    window.history.pushState({ modalOpened: false }, '');
  };

  return {
    isModalOpen,
    confirmReturn,
    cancelReturn,
    setIsModalOpen,
    handleReturn,
  };
}
